import { defineStore } from 'pinia';
import { type Shape } from '../domain/shape';
import { useEntityStore } from '../../common/composables/entityStore';

export const useShapeStore = defineStore('shapes', () => {
  const { add, remove, getById, getOrThrow, merge, changed, list, clear } = useEntityStore<Shape>({
    eventName: 'shape-changed',
    getEntityId(entity) {
      return entity.id;
    },
    insertEntity(entity) {
      addStorageAreaToShape(entity.storageAreaId, entity.id);
    },
    beforeUpdateEntity: (entity) => {
      removeStorageAreaToShape(entity.storageAreaId, entity.id);
    },
    updateEntity(dest, src) {
      dest.isBackground = src.isBackground;
      dest.storageAreaId = src.storageAreaId;
      dest.updatedAtUtc = src.updatedAtUtc;
      dest.version = src.version;
      dest.geofenceGeoJson = src.geofenceGeoJson;
      dest.shapeGeoJson = src.shapeGeoJson;
      dest.color = src.color;
    },
    afterUpdateEntity: (entity) => {
      addStorageAreaToShape(entity.storageAreaId, entity.id);
    },
    removeEntity(entity) {
      removeStorageAreaToShape(entity.storageAreaId, entity.id);
    },
  });

  const storageAreaToShapeIds = new Map<string, Set<string>>();

  return {
    add,
    remove,
    getById,
    getOrThrow,
    merge,
    clear,
    getByStorageAreaId,
    shapes: list.value,
    changed,
  };

  function addStorageAreaToShape(storageAreaId: string | null, shapeId: string) {
    if (!storageAreaId) return;
    let shapeIds = storageAreaToShapeIds.get(storageAreaId);
    if (!shapeIds) {
      shapeIds = new Set();
      storageAreaToShapeIds.set(storageAreaId, shapeIds);
    }
    shapeIds.add(shapeId);
  }

  function removeStorageAreaToShape(storageAreaId: string | null, shapeId: string) {
    if (!storageAreaId) return;
    const shapeIds = storageAreaToShapeIds.get(storageAreaId);
    if (!shapeIds) return;
    shapeIds.delete(shapeId);
    if (shapeIds.size > 0) return;
    storageAreaToShapeIds.delete(storageAreaId);
  }

  function getByStorageAreaId(storageAreaId: string): Shape[] {
    const shapeIds = storageAreaToShapeIds.get(storageAreaId);
    const result: Shape[] = [];
    if (!shapeIds || shapeIds.size === 0) return result;
    for (const shapeId of shapeIds) {
      const shape = getById(shapeId);
      if (!shape) continue;
      result.push(shape);
    }
    return result;
  }
});
